import React, { useCallback } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { Labeled } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import SelectField from "@app/component/input/SelectField";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { updateHeatMapActivityTypes } from "@app/scene/map/store/actions";
import HeatMapLayerData from "@app/scene/map/store/layers/heatMap/HeatMapLayerData";
import { getActiveActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";

import styles from "./ActivityHeatMapSettings.module.scss";

interface Props {
  activityTypes: ActivityType[];
  data: HeatMapLayerData;
  onUpdateHeatMapActivityTypes: (payload: ActivityType["id"][]) => void;
}

const ActivityHeatMapSettings: React.FC<Props> = ({
  activityTypes,
  data,
  onUpdateHeatMapActivityTypes,
}) => {
  const intl = useIntl();

  const handleChangeActivityTypes = useCallback(
    (activityTypeIds: ActivityType["id"][]) => {
      onUpdateHeatMapActivityTypes?.(
        activityTypeIds.length === activityTypes.length ? [] : activityTypeIds
      );
    },
    [activityTypes, onUpdateHeatMapActivityTypes]
  );

  return (
    <Labeled
      label={intl.formatMessage({
        id: "map.layer.heatMap.edit.activityTypes",
        defaultMessage: "Include the following Activity Types",
        description: "Activity types selection in Edit Heat Map panel on the Map page",
      })}
    >
      {/* TODO: extract into its own component in /src/component/input */}
      <SelectField<ActivityType["id"][]>
        className={styles.activityTypeSelect}
        filterOption={defaultFilterOption}
        maxTagCount="responsive"
        mode="multiple"
        onChange={handleChangeActivityTypes}
        value={
          data.activityTypeIds.length ? data.activityTypeIds : activityTypes.map(({ id }) => id)
        }
      >
        {activityTypes.map((activityType) => (
          <Select.Option key={activityType.id} label={activityType.name} value={activityType.id}>
            <ActivityTypeIcon activityType={activityType} size="sm" />
            <span className={styles.activityTypeText}>{activityType.name}</span>
          </Select.Option>
        ))}
      </SelectField>
    </Labeled>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActiveActivityTypes(state),
});

const mapDispatchToProps = {
  onUpdateHeatMapActivityTypes: updateHeatMapActivityTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityHeatMapSettings);
